import { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    Grid,
    Avatar,
    Chip,
    Divider,
    Fab
} from '@mui/material'
import {
    CalendarToday as CalendarIcon,
    Pets as PetsIcon,
    Home as HomeIcon,
    People as PeopleIcon,
    CalendarMonth as CalendarMonthIcon,
    Person as PersonIcon,
    School as SchoolIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material'

interface ProfileData {
    // Basic Info
    first_name: string;
    last_initial: string;
    profile_pic_url: string | null;
    short_intro: string;
    prospectId: string;

    // Employment
    occupation: string;
    employer: string;

    // Rental Preferences
    budget: string;
    bedrooms: string;
    bathrooms: string;
    desired_move_in: string;
    lease_length: string;
    rental_type?: string;
    tour_preference?: string;
    occupants: string;
    neighborhoods: string;
    additionalInfo: string;

    // Personal info
    age_range?: string;
    student?: boolean;
    undergraduate?: boolean;
    current_living_situation?: string;
    moving_reason?: string;

    // Screening info
    will_do_background_and_credit_check?: boolean;
    has_current_job_for_year?: boolean;
    has_year_long_rental?: boolean;
    income_verifiable?: boolean;

    // Pet and housing info
    housingVoucher: string;
    numDogs: string;
    numCats: string;
}

interface ProfileCardProps {
    profileData: ProfileData;
    hideRequestTourButton?: boolean;
}

const INTRO_PREVIEW_LENGTH = 50;

export default function ProfileCard({ profileData, hideRequestTourButton }: ProfileCardProps) {
    const [introExpanded, setIntroExpanded] = useState(false);
    const introIsLong = profileData.short_intro && profileData.short_intro.length > INTRO_PREVIEW_LENGTH;

    return (
        <>
            <Card sx={{ p: 4, mb: 3, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.2)' }}>
            {/* Basic Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                    src={profileData.profile_pic_url || undefined}
                    sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'grey.300',
                        fontSize: '3rem',
                        mr: 3
                    }}
                >
                    {!profileData.profile_pic_url && (profileData.first_name?.[0] || '?')}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {profileData.first_name} {profileData.last_initial}.
                    </Typography>
                    {profileData.occupation && (
                        <Typography variant="h6" color="grey.600" sx={{ mb: 1 }}>
                            {profileData.occupation}
                        </Typography>
                    )}
                    {profileData.employer && (
                        <Typography variant="body1" color="grey.500">
                            {profileData.employer}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Introduction */}
            {profileData.short_intro && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        About Me
                    </Typography>
                    <Typography variant="body1" sx={{ mb: introIsLong ? 1 : 3, lineHeight: 1.6 }}>
                        {introExpanded || !introIsLong
                            ? profileData.short_intro
                            : `${profileData.short_intro.slice(0, INTRO_PREVIEW_LENGTH).trimEnd()}...`}
                    </Typography>
                    {introIsLong && (
                        <Typography
                            variant="body2"
                            onClick={() => setIntroExpanded(prev => !prev)}
                            sx={{
                                mb: 3,
                                color: 'primary.main',
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {introExpanded ? 'Show less' : 'Read more'}
                        </Typography>
                    )}
                    <Divider sx={{ my: 3 }} />
                </>
            )}

            {/* Personal Info */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {profileData.age_range && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Age Range
                            </Typography>
                        </Box>
                        <Typography variant="h6">
                            {profileData.age_range}
                        </Typography>
                    </Grid>
                )}
                {profileData.student && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <SchoolIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Student
                            </Typography>
                        </Box>
                        <Chip label="Yes" color="info" variant="outlined" />
                    </Grid>
                )}
                {profileData.current_living_situation && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Current Living Situation
                            </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {profileData.current_living_situation.replace(/_/g, ' ')}
                        </Typography>
                    </Grid>
                )}
                {profileData.moving_reason && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <SwapHorizIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Moving Reason
                            </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {profileData.moving_reason.replace(/_/g, ' ')}
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PeopleIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                        <Typography variant="body2" color="grey.600">
                            Applicants
                        </Typography>
                    </Box>
                    <Typography variant="h6">
                        {profileData.occupants || 'Just me'}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PetsIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                        <Typography variant="body2" color="grey.600">
                            Pets
                        </Typography>
                    </Box>
                    <Typography variant="h6">
                        {Number(profileData.numDogs) === 0 && Number(profileData.numCats) === 0
                            ? 'No pets'
                            : [
                                Number(profileData.numDogs) > 0 && `${profileData.numDogs} dog${Number(profileData.numDogs) !== 1 ? 's' : ''}`,
                                Number(profileData.numCats) > 0 && `${profileData.numCats} cat${Number(profileData.numCats) !== 1 ? 's' : ''}`
                              ].filter(Boolean).join(' • ')}
                    </Typography>
                </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />

            {/* Rental Preferences */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Rental Preferences
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                        <Typography variant="body2" color="grey.600">
                            Tour Preference
                        </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                        {profileData.tour_preference ? profileData.tour_preference.replace('_', ' ') : 'Not specified'}
                    </Typography>
                </Grid>
                {profileData.rental_type && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Rental Type
                            </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {profileData.rental_type.replace('_', ' ')}
                        </Typography>
                    </Grid>
                )}
                {(profileData.bedrooms || profileData.bathrooms) && (
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Looking for
                            </Typography>
                        </Box>
                        <Typography variant="h6">
                            {profileData.bedrooms || '–'} bed • {profileData.bathrooms || '–'} bath
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                        <Typography variant="body2" color="grey.600">
                            Ideal Move-in Date
                        </Typography>
                    </Box>
                    <Typography variant="h6">
                        {profileData.desired_move_in ? new Date(`${profileData.desired_move_in}T12:00:00`).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric'
                        }) : 'Not specified'}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                        <Typography variant="body2" color="grey.600">
                            Lease Length
                        </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                        {profileData.lease_length ? profileData.lease_length.replace('-', ' ') : 'Not specified'}
                    </Typography>
                </Grid>

            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Additional Details */}
            <Grid container spacing={3}>
                {(profileData.housingVoucher === 'yes' ||
                  profileData.will_do_background_and_credit_check ||
                  profileData.has_current_job_for_year ||
                  profileData.has_year_long_rental ||
                  profileData.income_verifiable ||
                  (profileData.student && profileData.undergraduate)) && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="grey.600" sx={{ mb: 1.5 }}>
                            Highlights
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {profileData.housingVoucher === 'yes' && (
                                <Chip label="Housing Voucher" color="success" variant="outlined" />
                            )}
                            {profileData.will_do_background_and_credit_check && (
                                <Chip label="Will do background & credit check" color="success" variant="outlined" />
                            )}
                            {profileData.has_current_job_for_year && (
                                <Chip label="Employed 1+ year" color="info" variant="outlined" />
                            )}
                            {profileData.has_year_long_rental && (
                                <Chip label="1+ year rental history" color="info" variant="outlined" />
                            )}
                            {profileData.income_verifiable && (
                                <Chip label="Income verifiable" color="success" variant="outlined" />
                            )}
                            {profileData.student && profileData.undergraduate && (
                                <Chip label="Undergraduate" color="secondary" variant="outlined" />
                            )}
                        </Box>
                    </Grid>
                )}

                {profileData.neighborhoods && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                            Preferred Neighborhoods
                        </Typography>
                        <Typography variant="body1">
                            {profileData.neighborhoods}
                        </Typography>
                    </Grid>
                )}

                {profileData.additionalInfo && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                            Additional Information
                        </Typography>
                        <Typography variant="body1">
                            {profileData.additionalInfo}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Card>

        {!hideRequestTourButton && (
            <Fab
                variant="extended"
                color="primary"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '90%',
                    maxWidth: '400px',
                    bgcolor: 'black',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                        bgcolor: 'grey.800',
                        boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
                    },
                    zIndex: 1000
                }}
                onClick={() => {
                    window.open(`${window.location.origin}/request-tour/${profileData.prospectId}`, '_blank');
                }}
            >
                <CalendarMonthIcon sx={{ mr: 1 }} />
                Request Tour w/ {profileData.first_name}
            </Fab>
        )}
        </>
    )
}
